import React, { useState, useEffect, useRef } from "react";
import sanityClient from "../client";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import heroVideo from "../assets/images/video.mp4";
import Reveal, { RevealGroup, RevealItem } from "./ui/Reveal";
import Parallax from "./ui/Parallax";
import Counter from "./ui/Counter";
import Marquee from "./ui/Marquee";
import { fetchStories, storyAnchorId, initialsOf } from "../lib/stories";
import { fetchPress, formatPressDate, scanUrl } from "../lib/press";
import "./styles/Home.scss";

const STATS = [
  // Fixed year rather than a running count, so it can never go stale.
  { to: 2010, display: "2010", label: "Serving students since" },
  { to: 500, suffix: "+", label: "Young musicians mentored" },
  { to: 1, prefix: "", suffix: "", label: "World-class recording studio", display: "Pro" },
  { to: 100, suffix: "%", label: "Free to every student" },
];

const MARQUEE = [
  "Create",
  "Record",
  "Perform",
  "Mentor",
  "Inspire",
  "Produce",
  "Believe",
];

export default function Home() {
  const [stories, setStories] = useState([]);
  const navigate = useNavigate();
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const overlayDarken = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  useEffect(() => {
    fetchStories().then(setStories).catch(console.error);
  }, []);

  return (
    <div className="home">
      {/* ===================== HERO ===================== */}
      <section className="hero" ref={heroRef}>
        <motion.div className="hero__media" style={{ scale: videoScale }}>
          <video autoPlay loop muted playsInline className="hero__video">
            <source src={heroVideo} type="video/mp4" />
          </video>
        </motion.div>
        <motion.div
          className="hero__scrim"
          style={{ "--scrim": overlayDarken }}
        />
        <div className="hero__grain" aria-hidden="true" />

        <motion.div className="hero__inner" style={{ y: heroTextY, opacity: heroFade }}>
          <motion.span
            className="hero__eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Del Couch Music Education Foundation
          </motion.span>

          <h1 className="hero__title">
            <Line delay={0.35}>Where young voices</Line>
            <Line delay={0.5}>
              become <em>music.</em>
            </Line>
          </h1>

          <motion.p
            className="hero__tagline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
          >
            Free, world-class music education, recording, and mentorship for the next
            generation of artists.
          </motion.p>

          <motion.div
            className="hero__actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
          >
            <button className="btn btn--gold" onClick={() => navigate("/donations")}>
              Support a student
            </button>
            <button className="btn btn--ghost" onClick={() => navigate("/success-stories")}>
              ▶ See their stories
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero__scroll-cue"
          style={{ opacity: heroFade }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
        >
          <span>Scroll</span>
          <span className="hero__scroll-line" />
        </motion.div>
      </section>

      {/* ===================== MARQUEE ===================== */}
      <div className="home__marquee-band">
        <Marquee items={MARQUEE} speed={28} />
      </div>

      {/* ===================== ABOUT / MISSION ===================== */}
      <section className="mission" id="about">
        <div className="mission__inner">
          <Parallax speed={40} className="mission__media">
            <div className="mission__video-frame">
              <video autoPlay loop muted playsInline>
                <source src={heroVideo} type="video/mp4" />
              </video>
              <span className="mission__video-tag">Inside the studio</span>
            </div>
          </Parallax>

          <div className="mission__text">
            <Reveal>
              <span className="eyebrow">Our mission</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mission__title">
                Music has the power to <em>change a life.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mission__body">
                The Del Couch Music Education Foundation gives students the
                opportunity to create, record, and understand music — and the
                business behind it. We provide a platform that enhances their
                musical, cultural, academic, and life opportunities.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <blockquote className="mission__quote">
                “Those students who come to us share the passion — to express
                themselves, to be heard, to create, and sometimes just to sing
                out. It's a passion they'll carry throughout their lives.”
                <cite>— Del Couch</cite>
              </blockquote>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/about" className="text-link">
                Read our full story →
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== STATS ===================== */}
      <section className="stats">
        <RevealGroup className="stats__grid">
          {STATS.map((s) => (
            <RevealItem className="stats__item" key={s.label}>
              <span className="stats__num">
                {s.prefix || ""}
                {s.display ? s.display : <Counter to={s.to} suffix={s.suffix} />}
                {!s.display && ""}
              </span>
              <span className="stats__label">{s.label}</span>
            </RevealItem>
          ))}
        </RevealGroup>
      </section>

      {/* ===================== SUCCESS STORIES ===================== */}
      <section className="stories" id="success-stories">
        <div className="stories__head">
          <Reveal>
            <span className="eyebrow">Their impact</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="section-title">Success stories</h2>
          </Reveal>
        </div>

        <div className="stories__grid">
          {stories.length > 0 ? (
            stories.slice(0, 6).map((story, index) => (
              <Reveal
                key={story._id}
                delay={(index % 3) * 0.08}
                className={`story-cell ${index % 5 === 0 ? "story-cell--wide" : ""}`}
              >
                <Link
                  to={`/success-stories#${storyAnchorId(story._id)}`}
                  className="story-card"
                  aria-label={story.name ? `Read ${story.name}'s story` : "Read story"}
                >
                  <div className="story-card__media">
                    {story.images[0] ? (
                      <img src={story.images[0].url} alt={story.name || ""} loading="lazy" />
                    ) : (
                      <div className="story-card__initials" aria-hidden="true">
                        <span>{initialsOf(story.name)}</span>
                      </div>
                    )}
                  </div>
                  <span className="story-card__cue" aria-hidden="true">↗</span>
                </Link>
              </Reveal>
            ))
          ) : (
            <p className="stories__empty">More stories coming soon.</p>
          )}
        </div>

        <Reveal className="stories__cta-wrap">
          <Link to="/success-stories" className="btn btn--ghost">
            View all success stories
          </Link>
        </Reveal>
      </section>

      {/* ===================== UPCOMING EVENTS ===================== */}
      <UpcomingEvents />

      {/* ===================== IN THE NEWS ===================== */}
      <InTheNews />

      {/* ===================== DONATE CTA ===================== */}
      <section className="donate-cta">
        <div className="donate-cta__media">
          <video autoPlay loop muted playsInline>
            <source src={heroVideo} type="video/mp4" />
          </video>
        </div>
        <div className="donate-cta__scrim" />
        <div className="donate-cta__inner">
          <Reveal>
            <h2 className="donate-cta__title">
              Give a young musician their <em>first stage.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="donate-cta__text">
              Every gift funds instruments, studio time, and mentorship — at no
              cost to the students who need it most.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/donations" className="btn btn--gold btn--lg">
              Donate today
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

// Animated headline line
function Line({ children, delay }) {
  return (
    <span className="hero__title-line">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    sanityClient
      .fetch(
        // Only events that have not happened yet, soonest first. Without this an
        // event stays under "Upcoming" forever — the two in the CMS were from 2025.
        `*[_type == "upcomingEvents" && dateTime(date) >= dateTime(now())] | order(date asc) {
          _id,
          title,
          description,
          date,
          "imageUrl": image.asset->url
        }`
      )
      .then((data) => setEvents(data))
      .catch(console.error);
  }, []);

  const goPrev = () => setCurrentIndex((i) => (i === 0 ? events.length - 1 : i - 1));
  const goNext = () => setCurrentIndex((i) => (i === events.length - 1 ? 0 : i + 1));

  if (events.length === 0) return null;

  const current = events[currentIndex];
  return (
    <section className="events" id="upcoming-events">
      <div className="events__head">
        <Reveal>
          <span className="eyebrow">What's next</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="section-title">Upcoming events</h2>
        </Reveal>
      </div>

      <Reveal className="events__carousel">
        <button className="events__arrow" onClick={goPrev} aria-label="Previous">
          ‹
        </button>
        <motion.div
          className="events__slide"
          key={current._id}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <img
            src={current.imageUrl}
            alt={current.title || "Event"}
            onClick={() => window.open(current.imageUrl, "_blank")}
          />
          {(current.title || current.date) && (
            <div className="events__caption">
              {current.title && <h3>{current.title}</h3>}
              {current.date && <span>{current.date}</span>}
            </div>
          )}
        </motion.div>
        <button className="events__arrow" onClick={goNext} aria-label="Next">
          ›
        </button>
      </Reveal>

      {events.length > 1 && (
        <div className="events__dots">
          {events.map((e, i) => (
            <button
              key={e._id}
              className={`events__dot ${i === currentIndex ? "is-active" : ""}`}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Go to event ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// Press coverage strip. Prefers items flagged "featured" in the CMS and falls
// back to the most recent few, so the section never renders empty once any
// press item exists.
function InTheNews() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchPress().then((all) => {
      if (!alive) return;
      const featured = all.filter((a) => a.featured);
      setItems((featured.length ? featured : all).slice(0, 3));
    });
    return () => {
      alive = false;
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="news" id="in-the-news">
      <div className="news__head">
        <Reveal>
          <span className="eyebrow">In the news</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="section-title">
            What people are <em>writing</em> about us
          </h2>
        </Reveal>
      </div>

      <RevealGroup className="news__grid">
        {items.map((item) => (
          <RevealItem key={item._id}>
            <Link to="/press" className="news-card">
              <div className="news-card__media">
                {item.scans && item.scans[0] ? (
                  <img
                    src={scanUrl(item.scans[0].url, 640)}
                    alt={item.scans[0].caption || item.title}
                    loading="lazy"
                  />
                ) : (
                  // Link-only items still need a tile so the grid stays even.
                  <div className="news-card__placeholder" aria-hidden="true">
                    <span>{item.publication}</span>
                  </div>
                )}
              </div>
              <div className="news-card__body">
                <p className="news-card__meta">
                  {item.publication}
                  {item.date && (
                    <>
                      <span aria-hidden="true"> · </span>
                      {formatPressDate(item.date, item.approximateDate)}
                    </>
                  )}
                </p>
                <h3 className="news-card__title">{item.title}</h3>
                {item.excerpt && <p className="news-card__excerpt">{item.excerpt}</p>}
                <span className="news-card__cue">Read more →</span>
              </div>
            </Link>
          </RevealItem>
        ))}
      </RevealGroup>

      <Reveal className="news__cta-wrap">
        <Link to="/press" className="btn btn--ghost">
          All press &amp; news
        </Link>
      </Reveal>
    </section>
  );
}
